import {
  SectionSubHeading,
  SectionMessage,
  Popover,
  PopoverContent,
  DialogTrigger,
  GhostIconButton,
} from "@certn/cascadia-react";
import { RenderContext } from "@certn/cascadia-react/context/RenderContext";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { LayoutProps } from "@jsonforms/core";
import { withJsonFormsLayoutProps } from "@jsonforms/react";
import { PropsWithChildren, useContext } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";

import { BaseLayout } from "./BaseLayout";
import { BaseLayoutContent } from "./BaseLayoutContent";
import { Text } from "../components/Text";
import { RenderError } from "../components/helpers/RenderError";
import { ReportLayoutMessage } from "../types";
import { isReportLayout } from "../utils";

function convertTestId(title: string) {
  const title_without_count = title.replace(/[^A-Za-z\s]+/gi, "");
  return slugify(title_without_count.trim());
}

export function ReportLayout(props: LayoutProps) {
  const { uischema } = props;
  const options = uischema.options || {};
  const { t } = useTranslation();
  const isPDF = useContext(RenderContext).isForPdfGeneration;

  if (!isReportLayout(uischema)) {
    return <RenderError>Invalid Report Section</RenderError>;
  }

  const hasSectionHeader = !!uischema.label;

  const textWrapper = (props: PropsWithChildren) => {
    return <Text className={styles.popoverContent}>{props.children}</Text>;
  };
  const title = props.label ? props.label.toString() : "";
  return (
    <BaseLayout
      testID={`${convertTestId(title)}-report-section-layout`}
      {...props}
      className={css({
        gap: options.gap ? `var(--${options.gap})` : undefined,
      })}
    >
      <>
        {options.messages && (
          <>
            {options.messages.map(
              (message: ReportLayoutMessage, index: number) => {
                return (
                  <SectionMessage
                    key={index}
                    icon={message.icon}
                    type={message.type}
                    sectionTitle={message.title}
                  >
                    <Markdown
                      components={{
                        p: "span",
                        ul(props) {
                          const { node, ref, ...rest } = props;
                          return (
                            <ul className={styles.messagesList} {...rest} />
                          );
                        },
                      }}
                    >
                      {message.body}
                    </Markdown>
                  </SectionMessage>
                );
              }
            )}
          </>
        )}
        {hasSectionHeader ? (
          <>
            <SectionSubHeading
              title={uischema.label}
              tooltip={
                options.popoverContent &&
                !isPDF && (
                  <DialogTrigger>
                    <GhostIconButton
                      icon="info"
                      testID="text-display-info-button"
                      aria-label={t("Additional information")}
                      className={styles.popoverIcon}
                    />
                    <Popover placement="top">
                      <PopoverContent>
                        <Markdown
                          components={{
                            p: textWrapper,
                          }}
                        >
                          {options.popoverContent}
                        </Markdown>
                      </PopoverContent>
                    </Popover>
                  </DialogTrigger>
                )
              }
            />
            {options.subLabel && (
              <Text className={styles.subLabel}>{options.subLabel}</Text>
            )}
            <div className={styles.nestedContainer}>
              <BaseLayoutContent />
            </div>
          </>
        ) : (
          <BaseLayoutContent />
        )}
      </>
    </BaseLayout>
  );
}

const styles = {
  nestedContainer: css({
    padding: "0 var(--space-3)",
    "@media print": {
      marginLeft: 0,
      marginRight: 0,
    },
  }),
  subLabel: css({
    padding: "0 var(--space-3)",
  }),
  messagesList: css({
    paddingInlineStart: 21,
  }),
  popoverIcon: css({
    width: "1.1rem",
    height: "1.1rem",
    "& i": {
      color: "var(--icon-weak)",
    },
  }),
  popoverContent: css({ color: "var(--text-weak)" }),
};
export const ReportLayoutRenderer = withJsonFormsLayoutProps((props) => {
  const { data: _data, ...otherProps } = props;
  return <ReportLayout {...otherProps} />;
});
